import { Button, Dialog, IconButton, Stack } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { useAuthState } from 'state/useAuthState';
import { usePoisState } from 'state/usePoisState';
import { Poi, PoiMetadataByType } from 'shared/types';
import useSearchDrawerState from 'state/useSearchDrawerState';
import { MAP_DOM_ELEMENT_ID } from '../../constants';
import { getMapDialogCommonStyles } from './utils';
import { ActiveFirePerimeterDialogContent } from './ActiveFirePerimeterDialogContent';
import { ExternalGeoEventDialogContent } from './ExternalGeoEventDialog';
import { PlaceDialogContent } from './PlaceDialogContent';
import { PowerOutageDialogContent } from './PowerOutageDialogContent';
import { RadioTowerDialogContent } from './RadioTowerDialogContent';
import { RFWDialogContent } from './RFWDialogContent';
import { AircraftDialogContent } from './AircraftDialogContent';
import ElectricRetailDialogContent from './layers/ElectricRetailServiceLayer/DialogContent';
import ResponsibilityDialogContent from './layers/ResponsibilityAreasLayer/DialogContent';
import { EvacZoneDialogContent } from './EvacZoneDialogContent';
import ElectricLineDialogContent from './layers/TransmissionLinesLayer/DialogContent';
import { MeasureDistanceToolDialogContent } from './MeasureDistanceToolDialogContent';
import { useMapEntityDrawerState } from './MapEntityDrawer';

type DialogComponents = {
  [K in keyof PoiMetadataByType]: PoiMetadataByType[K] extends null
    ? () => JSX.Element
    : (props: PoiMetadataByType[K]) => JSX.Element;
};

const POI_DIALOG_COMPONENTS: DialogComponents = {
  firePerimeter: ActiveFirePerimeterDialogContent,
  externalGeoEvent: ExternalGeoEventDialogContent,
  place: PlaceDialogContent,
  powerOutage: PowerOutageDialogContent,
  radioTower: RadioTowerDialogContent,
  rfw: RFWDialogContent,
  aircraft: AircraftDialogContent,
  electricRetail: ElectricRetailDialogContent,
  responsibilityArea: ResponsibilityDialogContent,
  evacZone: EvacZoneDialogContent,
  activeEvacZone: EvacZoneDialogContent,
  electricalLine: ElectricLineDialogContent,
  gasPipeline: ElectricLineDialogContent,
  measureTool: MeasureDistanceToolDialogContent,
};

const PoiDialogContent = (poi: Poi): JSX.Element | null => {
  const { type, metadata } = poi;
  const PoiDialogComponent = POI_DIALOG_COMPONENTS[type];
  // The "key" prop is used to force a re-render of the component when the type changes
  if (type === 'measureTool') {
    // @ts-expect-error - we know metadata is undefined and component takes no props
    return <PoiDialogComponent key={type} />;
  }
  // @ts-expect-error - we know metadata is of the correct type
  return <PoiDialogComponent {...metadata} key={type} />;
};

const useStyles = makeStyles<{
  isDrawerEntitySelected: boolean;
  isSearchDrawerOpen: boolean;
  interactive: boolean;
  minSnapPct: number;
}>()((
  theme,
  { isDrawerEntitySelected, isSearchDrawerOpen, interactive, minSnapPct },
) => {
  return {
    ...getMapDialogCommonStyles(theme, {
      isDrawerEntitySelected,
      isSearchDrawerOpen,
      interactive,
      minWidth: 362,
      minSnapPct,
    }),
    closeButton: {
      position: 'absolute',
      top: 8,
      right: 8,
      pointerEvents: 'auto',
    },
  };
});

export const PoiDialog = (): JSX.Element | null => {
  const { open: isSearchDrawerOpen } = useSearchDrawerState();
  const {
    permissions: { canReport, isInternalUser },
  } = useAuthState();
  const { open: isDrawerEntitySelected, minSnapPct } =
    useMapEntityDrawerState();
  const { classes } = useStyles({
    isDrawerEntitySelected,
    isSearchDrawerOpen,
    interactive: canReport || isInternalUser,
    minSnapPct,
  });
  const {
    selectedPois,
    selectedPoi,
    activeIndex,
    clearSelectedPois,
    selectPreviousPoi,
    selectNextPoi,
  } = usePoisState();
  const { t } = useTranslation();

  return (
    <Dialog
      open={!!selectedPois.length}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      classes={{
        root: classes.root,
        container: classes.container,
        paper: classes.paper,
      }}
      hideBackdrop
      container={(): HTMLElement | null =>
        document.getElementById(MAP_DOM_ELEMENT_ID)
      }
      onClose={() => clearSelectedPois()}
    >
      {selectedPoi && <PoiDialogContent {...selectedPoi} />}

      {selectedPois.length > 1 && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: 'flex-end',
            mb: 1,
            mr: 1,
          }}
        >
          <Button
            variant="watchduty-text-uppercase"
            startIcon={<ChevronLeftIcon />}
            onClick={selectPreviousPoi}
            disabled={activeIndex === 0}
            sx={{
              color: 'text.primary',
              pointerEvents: 'auto',
            }}
          >
            {t('common.prev')}
          </Button>
          <Button
            variant="watchduty-text-uppercase"
            endIcon={<ChevronRightIcon />}
            onClick={selectNextPoi}
            disabled={activeIndex === selectedPois.length - 1}
            sx={{
              color: 'text.primary',
              pointerEvents: 'auto',
            }}
          >
            {t('common.next')}
          </Button>
        </Stack>
      )}
      <IconButton
        size="small"
        className={classes.closeButton}
        onClick={() => clearSelectedPois()}
      >
        <CloseIcon />
      </IconButton>
    </Dialog>
  );
};
